import { toast } from '@skillarea/lib-frontend';
import {
  deleteCookie,
  getCookie,
  IS_LOGGED_COOKIE_NAME,
  MFA_PENDING_COOKIE_NAME,
} from '@skillarea/lib-shared';
import { createStore, useStore } from 'zustand';

import i18n from '~/common/i18n';
import { OnboardService } from '~/services/onboardService';
import { clearStore } from '~/stores/clear';

type TSignIn = typeof OnboardService.prototype.signIn;
type TSignOut = typeof OnboardService.prototype.signOut;
type TConfirmCode = typeof OnboardService.prototype.confirmLinkActivation;
type TRegister = typeof OnboardService.prototype.register;
type TRestorePassword = typeof OnboardService.prototype.restorePassword;
type TCheckChangePasswordLink =
  typeof OnboardService.prototype.checkChangePasswordLink;
type TChangePassword = typeof OnboardService.prototype.changePassword;
type TCheckLinkActivation = typeof OnboardService.prototype.checkLinkActivation;
type TResendLinkActivation =
  typeof OnboardService.prototype.resendLinkActivation;
type TSetupTotp = typeof OnboardService.prototype.setupTotp;
type TVerifyTotp = typeof OnboardService.prototype.verifyTotp;
type TEnableTotp = typeof OnboardService.prototype.enableTotp;
type TDisableTotp = typeof OnboardService.prototype.disableTotp;

const service = new OnboardService();

type TOnboardState = {
  isLoggedIn: boolean;

  clearClientSession: () => void;
  updateIsLoggedIn: () => void;
  signIn: (...args: Parameters<TSignIn>) => Promise<unknown>;
  signOut: (...args: Parameters<TSignOut>) => Promise<unknown>;
  signOutWithConfirm: () => Promise<void>;
  confirmLinkActivation: (
    ...args: Parameters<TConfirmCode>
  ) => Promise<unknown>;
  register: (...args: Parameters<TRegister>) => Promise<unknown>;
  restorePassword: (...args: Parameters<TRestorePassword>) => Promise<unknown>;
  checkChangePasswordLink: (
    ...args: Parameters<TCheckChangePasswordLink>
  ) => Promise<unknown>;
  changePassword: (...args: Parameters<TChangePassword>) => Promise<unknown>;
  checkLinkActivation: (
    ...args: Parameters<TCheckLinkActivation>
  ) => Promise<unknown>;
  resendLinkActivation: (
    ...args: Parameters<TResendLinkActivation>
  ) => Promise<unknown>;
  setupTotp: (...args: Parameters<TSetupTotp>) => Promise<unknown>;
  verifyTotp: (...args: Parameters<TVerifyTotp>) => Promise<unknown>;
  enableTotp: (...args: Parameters<TEnableTotp>) => Promise<unknown>;
  disableTotp: (...args: Parameters<TDisableTotp>) => Promise<unknown>;
};

export const onboardStore = createStore<TOnboardState>((set, get) => ({
  isLoggedIn: Boolean(getCookie(IS_LOGGED_COOKIE_NAME)),

  clearClientSession() {
    if (typeof window === 'undefined') return;

    deleteCookie(IS_LOGGED_COOKIE_NAME);
    deleteCookie(MFA_PENDING_COOKIE_NAME);
    clearStore.clear();
    get().updateIsLoggedIn();
  },

  updateIsLoggedIn() {
    set({
      isLoggedIn: Boolean(getCookie(IS_LOGGED_COOKIE_NAME)),
    });
  },

  async signIn(...args) {
    const result = await service.signIn(...args);
    get().updateIsLoggedIn();
    return result;
  },

  async signOutWithConfirm() {
    // No modal for now
    await get().signOut();
  },

  async signOut(...args) {
    if (typeof window === 'undefined') return undefined;

    const result = await service.signOut(...args);
    get().clearClientSession();
    return result;
  },

  async confirmLinkActivation(...args) {
    const result = await service.confirmLinkActivation(...args);
    get().updateIsLoggedIn();

    if (
      result &&
      typeof result === 'object' &&
      'success' in result &&
      result.success === true
    ) {
      if (toast) toast.success(i18n.t('emailVerified', { ns: 'onboard' }));
    }

    return result;
  },

  register: (...args) => service.register(...args),

  restorePassword: (...args) => service.restorePassword(...args),

  checkChangePasswordLink: (...args) =>
    service.checkChangePasswordLink(...args),

  changePassword: (...args) => service.changePassword(...args),

  checkLinkActivation: (...args) => service.checkLinkActivation(...args),

  resendLinkActivation: (...args) => service.resendLinkActivation(...args),

  setupTotp: (...args) => service.setupTotp(...args),

  async verifyTotp(...args) {
    const result = await service.verifyTotp(...args);
    get().updateIsLoggedIn();
    return result;
  },

  enableTotp: (...args) => service.enableTotp(...args),

  disableTotp: (...args) => service.disableTotp(...args),
}));

export const useOnboardStore = <T>(selector: (s: TOnboardState) => T) =>
  useStore(onboardStore, selector);

export const useIsLoggedIn = () => useOnboardStore((s) => s.isLoggedIn);
